import { saveExportOnAndroid } from './androidBridge';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase, DEMO_ALLOWED, API_BASE } from './client';
import {
  COMPANY_DEMO_KEY,
  DEMO_KEY,
  DEMO_PREVIEW_KEY,
  createDemoState,
  readDemoState,
  serializeDemoState,
  clearStoredDemoImages,
  demoPreviewRequested,
} from './demo';
import type { AppAction, AppState, Member, Product, Review, Post, Conversation } from './types';
import { loadLiveState, persistChanges } from './sync';
import { rebaseValue } from './rebase';
import { exportDemoAccount } from './export';
import { clearGymCovers } from './gymCover';
import { clearGymGalleries, clearLegacyGymGalleryImages } from './gymGallery';
import { clearGymComments } from './gymComments';
import { clearAvatars, refreshAvatars } from './avatar';
import { ensureDemoGymComments } from './demoGymComments';
import { noStaffAccess } from './staffAccess';
import { createOwnAvatarRefresh, mergeOwnAvatar } from './avatarRefresh';
import { createAccountLifecycle } from './accountLifecycle';
import { addToast, newMessageNotices, type Toast } from './toasts';
import { createNotificationRefresh } from './notificationRefresh';
import { invalidateConversations } from './conversationInvalidation';
interface AppContextValue {
  state: AppState;
  currentUser: Member;
  mode: 'demo' | 'live' | 'unconfigured';
  session: Session | null;
  loading: boolean;
  accountReady: boolean;
  error: string | null;
  toasts: Toast[];
  dispatch: (action: AppAction) => void;
  save: (patch: Partial<AppState>) => Promise<boolean>;
  notify: (message: string, kind?: 'success' | 'error' | 'info') => void;
  navigate: (path: string) => void;
  reload: () => Promise<void>;
  dismissToast: (id: number) => void;
  requestExport: () => Promise<void>;
  requestDeletion: () => Promise<void>;
  hydrateProducts: (items: Product[]) => void;
  hydrateReviews: (items: Review[]) => void;
  hydrateCommunity: (partial: Partial<Pick<AppState, 'members' | 'topics' | 'listings'>>) => void;
  hydrateFeed: (items: Post[]) => void;
  removeFeedPosts: (ids: string[]) => void;
  hydrateConversations: (items: Conversation[]) => void;
  hydrateFollows: (
    items: AppState['follows'],
    replacePair?: { follower_id: string; following_id: string },
  ) => void;
}
const AppContext = createContext<AppContextValue | null>(null);
export function emptyState(userId = ''): AppState {
  return {
    version: 1,
    current_user_id: userId,
    members: [],
    products: [],
    owned_items: [],
    reviews: [],
    posts: [],
    stories: [],
    follows: [],
    blocks: [],
    mutes: [],
    forum_categories: [],
    topics: [],
    listings: [],
    conversations: [],
    notifications: [],
    notification_preferences: { activity_email: false },
    reports: [],
    audit_events: [],
    catalog_view: 'grid',
    demo_role: 'member',
    staff_access: { ...noStaffAccess },
  };
}
export function AppProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [preview] = useState(() => {
    let saved = false;
    try {
      saved = sessionStorage.getItem(DEMO_PREVIEW_KEY) === '1';
    } catch {}
    const active = demoPreviewRequested(
      window.location.search,
      DEMO_ALLOWED,
      saved,
      Boolean(supabase),
    );
    try {
      if (active) sessionStorage.setItem(DEMO_PREVIEW_KEY, '1');
      else sessionStorage.removeItem(DEMO_PREVIEW_KEY);
    } catch {}
    return active;
  });
  const mode = supabase ? 'live' : preview || DEMO_ALLOWED ? 'demo' : 'unconfigured';
  const [state, setState] = useState<AppState>(() => {
    if (mode !== 'demo') return emptyState();
    const demo = readDemoState();
    try {
      ensureDemoGymComments(demo.members);
    } catch {}
    return demo;
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  const [session, setSession] = useState<Session | null>(null),
    [loading, setLoading] = useState(mode === 'live'),
    [accountReady, setAccountReady] = useState(mode !== 'live'),
    [error, setError] = useState<string | null>(null),
    [toasts, setToasts] = useState<Toast[]>([]);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const account = useRef<ReturnType<typeof createAccountLifecycle> | null>(null);
  const accountIdentity = account.current?.version() ?? 0;
  function enqueueToast(message: string, kind: Toast['kind'], chat?: Toast['chat']) {
    const id = Date.now() + Math.random();
    setToasts((items) => addToast(items, { id, message, kind, chat }));
    setTimeout(() => setToasts((items) => items.filter((i) => i.id !== id)), 6000);
  }
  function notify(message: string, kind: Toast['kind'] = 'success') {
    enqueueToast(message, kind);
  }
  async function reload() {
    await account.current?.refresh();
  }
  useEffect(() => {
    clearAvatars();
    clearGymCovers();
    clearLegacyGymGalleryImages();
    clearStoredDemoImages();
    const refresh = () => refreshAvatars();
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);
  useEffect(() => {
    const client = supabase;
    if (mode !== 'live' || !client) return;
    const lifecycle = createAccountLifecycle({
      getSession: async () => {
        const result = await client.auth.getSession();
        if (result.error) throw result.error;
        return result.data.session;
      },
      load: (userId, signal) => loadLiveState(client, userId, signal),
      getState: () => stateRef.current,
      empty: emptyState,
      commit: (next) => {
        stateRef.current = next;
        setState(next);
      },
      session: (next) => {
        sessionRef.current = next;
        setSession(next);
      },
      ready: setAccountReady,
      loading: setLoading,
      error: setError,
    });
    account.current = lifecycle;
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, next) =>
      lifecycle.acceptSession(next, event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED'),
    );
    void lifecycle.bootstrap();
    return () => {
      subscription.unsubscribe();
      lifecycle.dispose();
      if (account.current === lifecycle) account.current = null;
    };
  }, []);
  useEffect(() => {
    if (mode === 'demo') {
      try {
        localStorage.setItem(DEMO_KEY, serializeDemoState(state));
      } catch {
        notify('This browser could not save the demo. Changes will last for this visit.', 'error');
      }
    }
  }, [state, mode]);
  useEffect(() => {
    const client = supabase;
    if (mode !== 'live' || !client || !session || !accountReady) return;
    const userId = session.user.id,
      lifecycle = account.current,
      version = lifecycle?.version();
    const current = () =>
      Boolean(
        lifecycle &&
        account.current === lifecycle &&
        version !== undefined &&
        lifecycle.isCurrent(userId, version),
      );
    const listeningSince = Math.max(
      -Infinity,
      ...stateRef.current.notifications.map((notice) => Date.parse(notice.created_at)),
    );
    const notifications = createNotificationRefresh(client, userId, {
      getViewerId: () => (current() ? sessionRef.current?.user.id : undefined),
      getState: () => stateRef.current,
      commit: (next) => {
        for (const { notice, conversationId } of newMessageNotices(
          stateRef.current.notifications,
          next.notifications,
          listeningSince,
        )) {
          enqueueToast(notice.body, 'info', {
            conversationId,
            viewerId: userId,
            notificationId: notice.id,
          });
        }
        stateRef.current = next;
        setState(next);
      },
    });
    const visible = () => document.visibilityState !== 'hidden';
    const refresh = () => {
      if (current() && visible()) notifications.schedule();
    };
    const resume = () => {
      if (current() && visible()) {
        notifications.schedule();
        invalidateConversations(userId);
      }
    };
    const channel = client
      .channel(`member-${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${session.user.id}`,
        },
        refresh,
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const id = (payload.new as { conversation_id?: unknown }).conversation_id;
          if (current() && visible() && typeof id === 'string') invalidateConversations(userId, id);
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') resume();
      });
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      notifications.dispose();
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
      void client.removeChannel(channel);
    };
  }, [mode, session?.user.id, accountIdentity, accountReady]);
  useEffect(() => {
    const client = supabase;
    const userId = session?.user.id;
    if (mode !== 'live' || !client || !userId || state.current_user_id !== userId) return;
    const avatar = createOwnAvatarRefresh(client, userId, {
      getViewerId: () => sessionRef.current?.user.id,
      getState: () => stateRef.current,
      commit: (next) => {
        stateRef.current = next;
        setState(next);
      },
    });
    const refresh = () => void avatar.refresh();
    const channel = client
      .channel(`own-avatar-${userId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
        refresh,
      )
      .subscribe();
    window.addEventListener('focus', refresh);
    refresh();
    return () => {
      avatar.dispose();
      window.removeEventListener('focus', refresh);
      void client.removeChannel(channel);
    };
  }, [mode, session?.user.id, state.current_user_id]);
  async function save(patch: Partial<AppState>): Promise<boolean> {
    const baseline = stateRef.current;
    if (mode === 'unconfigured') {
      notify('Connect Supabase to save account data.', 'error');
      return false;
    }
    if (mode === 'demo') {
      const next = { ...baseline, ...patch };
      stateRef.current = next;
      setState(next);
      return true;
    }
    const client = supabase,
      activeSession = sessionRef.current,
      lifecycle = account.current,
      accountVersion = lifecycle?.version();
    if (!activeSession || !client || !lifecycle || accountVersion === undefined) {
      notify('Sign in to continue.', 'error');
      return false;
    }
    const stillCurrent = () =>
      account.current === lifecycle && lifecycle.isCurrent(activeSession.user.id, accountVersion);
    let ok = true;
    const task = queue.current.then(async () => {
      if (!stillCurrent()) {
        ok = false;
        return;
      }
      const prev = stateRef.current;
      const next = rebaseValue(baseline, { ...baseline, ...patch }, prev) as AppState;
      try {
        await persistChanges(client, prev, next, activeSession.user.id);
        if (!stillCurrent()) {
          ok = false;
          return;
        }
        // An avatar read can finish while this save is in flight. Keep that
        // newer pointer while committing the rest of the successfully saved work.
        const latestAvatar = stateRef.current.members.find(
          (member) => member.id === activeSession.user.id,
        );
        const rebased = rebaseValue(prev, next, stateRef.current) as AppState;
        const committed = latestAvatar
          ? mergeOwnAvatar(rebased, activeSession.user.id, latestAvatar.avatar_media_id ?? null)
          : rebased;
        stateRef.current = committed;
        setState(committed);
        setError(null);
      } catch (e) {
        ok = false;
        if (!stillCurrent()) return;
        const message = e instanceof Error ? e.message : 'The change could not be saved.';
        notify(message, 'error');
        await lifecycle.refresh();
      }
    });
    queue.current = task;
    await task;
    return ok;
  }
  function dispatch(action: AppAction) {
    if (action.type === 'RESET') {
      if (mode === 'demo') {
        clearGymCovers();
        clearGymGalleries();
        clearGymComments();
        clearAvatars();
        localStorage.removeItem(COMPANY_DEMO_KEY);
        const fresh = createDemoState();
        try {
          ensureDemoGymComments(fresh.members);
        } catch {}
        stateRef.current = fresh;
        setState(fresh);
        notify('Local demo reset.');
      }
      return;
    }
    if (action.type === 'ADD_REPORT') {
      void save({
        reports: [
          { ...action.payload, id: crypto.randomUUID(), status: 'open' },
          ...stateRef.current.reports,
        ],
      });
      return;
    }
    void save(action.payload);
  }
  async function accountRequest(endpoint: string, body: Record<string, unknown> = {}) {
    if (mode !== 'live' || !supabase || !session)
      throw new Error('A live signed-in account is required.');
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message ?? 'Request failed.');
    return result;
  }
  async function requestExport() {
    if (mode === 'demo') {
      const blob = new Blob([JSON.stringify(exportDemoAccount(state), null, 2)], {
        type: 'application/json',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'garagegymbros-demo-export.json';
      link.click();
      URL.revokeObjectURL(link.href);
      notify('Local demo export downloaded.');
      return;
    }
    try {
      const result = await accountRequest('/account/export');
      if (saveExportOnAndroid(result.data)) return;
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'garagegymbros-account-export.json';
      link.click();
      URL.revokeObjectURL(link.href);
      notify('Account export downloaded.');
    } catch (e) {
      notify((e as Error).message, 'error');
    }
  }
  async function requestDeletion() {
    if (mode === 'demo') {
      clearGymCovers();
      clearGymGalleries();
      clearGymComments();
      clearAvatars();
      localStorage.removeItem(COMPANY_DEMO_KEY);
      localStorage.removeItem(DEMO_KEY);
      const fresh = createDemoState();
      try {
        ensureDemoGymComments(fresh.members);
      } catch {}
      stateRef.current = fresh;
      setState(fresh);
      navigate('/');
      notify('Your local demo changes were erased. No real account existed.');
      return;
    }
    try {
      const result = await accountRequest('/account/requests', { kind: 'deletion' });
      notify(
        typeof result.data?.notice === 'string'
          ? result.data.notice
          : 'Deletion request received. Marco Lopez manually completes deletion 7–30 days after your request and emails you when it is complete. You can cancel while it is pending.',
      );
      await reload();
    } catch (e) {
      notify((e as Error).message, 'error');
    }
  }
  const currentUser = state.members.find((m) => m.id === state.current_user_id) ?? {
    id: state.current_user_id,
    display_name: 'Your account',
    username: 'member',
    bio: '',
    location: '',
    avatar_color: '#666666',
    initials: 'G',
    is_private: true,
    gym_name: 'My garage gym',
  };
  const hydrateProducts = useCallback((items: Product[]) => {
    const map = new Map(stateRef.current.products.map((p) => [p.id, p]));
    for (const p of items) map.set(p.id, p);
    const next = { ...stateRef.current, products: [...map.values()] };
    stateRef.current = next;
    setState(next);
  }, []);
  const hydrateReviews = useCallback((items: Review[]) => {
    const map = new Map(stateRef.current.reviews.map((p) => [p.id, p]));
    for (const p of items) map.set(p.id, p);
    const next = { ...stateRef.current, reviews: [...map.values()] };
    stateRef.current = next;
    setState(next);
  }, []);
  const hydrateCommunity = useCallback(
    (partial: Partial<Pick<AppState, 'members' | 'topics' | 'listings'>>) => {
      const next = { ...stateRef.current };
      for (const key of ['members', 'topics', 'listings'] as const) {
        const rows = partial[key];
        if (!rows) continue;
        const map = new Map<string, { id: string }>(
          (next[key] as { id: string }[]).map((row) => [row.id, row]),
        );
        for (const row of rows) map.set(row.id, row);
        (next[key] as { id: string }[]) = [...map.values()];
      }
      stateRef.current = next;
      setState(next);
    },
    [],
  );
  const removeFeedPosts = useCallback((ids: string[]) => {
    const drop = new Set(ids);
    const next = {
      ...stateRef.current,
      posts: stateRef.current.posts.filter((post) => !drop.has(post.id)),
    };
    stateRef.current = next;
    setState(next);
  }, []);
  const hydrateFeed = useCallback((items: Post[]) => {
    const map = new Map(stateRef.current.posts.map((p) => [p.id, p]));
    for (const p of items) map.set(p.id, p);
    const next = {
      ...stateRef.current,
      posts: [...map.values()].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    };
    stateRef.current = next;
    setState(next);
  }, []);
  const hydrateConversations = useCallback((items: Conversation[]) => {
    const map = new Map(stateRef.current.conversations.map((c) => [c.id, c]));
    for (const c of items) {
      const old = map.get(c.id);
      const messages = new Map((old?.messages || []).map((m) => [m.id, m]));
      for (const m of c.messages) messages.set(m.id, m);
      map.set(c.id, {
        ...c,
        messages: [...messages.values()].sort((a, b) => a.created_at.localeCompare(b.created_at)),
      });
    }
    const next = { ...stateRef.current, conversations: [...map.values()] };
    stateRef.current = next;
    setState(next);
  }, []);
  const hydrateFollows = useCallback(
    (items: AppState['follows'], replacePair?: { follower_id: string; following_id: string }) => {
      const map = new Map(
        stateRef.current.follows
          .filter(
            (f) =>
              !replacePair ||
              f.follower_id !== replacePair.follower_id ||
              f.following_id !== replacePair.following_id,
          )
          .map((f) => [`${f.follower_id}:${f.following_id}`, f]),
      );
      for (const f of items) map.set(`${f.follower_id}:${f.following_id}`, f);
      const next = { ...stateRef.current, follows: [...map.values()] };
      stateRef.current = next;
      setState(next);
    },
    [],
  );
  return (
    <AppContext.Provider
      value={{
        state,
        currentUser,
        mode,
        session,
        loading,
        accountReady,
        error,
        toasts,
        dispatch,
        save,
        notify,
        navigate,
        reload,
        dismissToast: (id) => setToasts((items) => items.filter((i) => i.id !== id)),
        requestExport,
        requestDeletion,
        hydrateProducts,
        hydrateReviews,
        hydrateCommunity,
        hydrateFeed,
        removeFeedPosts,
        hydrateConversations,
        hydrateFollows,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('AppProvider is required.');
  return ctx;
}
